"use client";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import SubAgreementModal, {
  API,
  fmt,
  cls,
  STATUS_STYLE,
  STATUS_LABEL,
} from "./SubAgreementModal";

const ACCENT = "#f97316";

// List of agreements on a work order. Everything else — create, edit, view,
// send, sign, reverse, complete, pay, delete — happens inside SubAgreementModal.
export default function SubAgreementsPanel({
  woId,
  headers,
  onChange,
  defaultUnion = "non-union",
}) {
  const [agreements, setAgreements] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  // null = closed · { agreement: null } = create · { agreement: row } = open
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/subagreements3/work-order/${woId}`, {
        headers,
      });
      setAgreements(r.data.agreements || []);
      setSummary(r.data.summary || null);
      setErr("");
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [woId]);

  useEffect(() => {
    load();
  }, [load]);

  const refreshAll = async () => {
    await load();
    onChange?.();
  };

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-3 border-b border-gray-700 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-white">
            Subcontractor Agreements
          </h3>
          <span className="text-[10px] bg-gray-700 text-gray-400 px-1.5 py-0.5 rounded-full font-semibold">
            {agreements.length}
          </span>
          {loading && (
            <span className="text-xs text-gray-600 animate-pulse">
              Loading…
            </span>
          )}
        </div>
        <div className="flex items-center gap-4 text-xs">
          {summary && (
            <>
              <span className="text-gray-400">
                Incurred:{" "}
                <span className="text-yellow-300 font-mono font-semibold">
                  ${fmt(summary.incurred)}
                </span>
              </span>
              <span className="text-gray-400">
                Committed:{" "}
                <span className="text-green-300 font-mono font-semibold">
                  ${fmt(summary.committed)}
                </span>
              </span>
              <span className="text-gray-400">
                Paid:{" "}
                <span className="text-blue-300 font-mono font-semibold">
                  ${fmt(summary.paid)}
                </span>
              </span>
            </>
          )}
          <button
            onClick={() => setModal({ agreement: null })}
            className="px-3 py-1.5 text-xs font-semibold text-white rounded-lg hover:brightness-110 transition-all"
            style={{ backgroundColor: ACCENT }}
          >
            + New Agreement
          </button>
        </div>
      </div>

      {/* List */}
      {err ? (
        <p className="px-5 py-6 text-sm text-red-400">{err}</p>
      ) : !loading && agreements.length === 0 ? (
        <p className="px-5 py-8 text-sm text-gray-500 text-center">
          No agreements yet. Create one to generate a signable PDF and record
          the cost.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-900">
              <tr>
                {[
                  "Agreement",
                  "Subcontractor",
                  "Type",
                  "Scheduled",
                  "Scope",
                  "Status",
                  "Subtotal",
                  "Total",
                  "Signed",
                  "",
                ].map((h, i) => (
                  <th
                    key={h || `c${i}`}
                    className={cls(
                      "px-3 py-2.5 text-gray-400 font-medium whitespace-nowrap",
                      ["Subtotal", "Total"].includes(h)
                        ? "text-right"
                        : "text-left",
                    )}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              {agreements.map((a, i) => {
                const reversalCount = (a.signatureReversals || []).length;
                return (
                  <tr
                    key={a._id}
                    onClick={() => setModal({ agreement: a })}
                    className={cls(
                      "cursor-pointer hover:bg-gray-600/30 transition-colors",
                      i % 2 === 0 ? "bg-gray-800/40" : "bg-gray-700/20",
                      a.status === "issued" &&
                        "border-l-2 border-yellow-500/50",
                    )}
                  >
                    <td className="px-3 py-2 font-mono text-orange-400 whitespace-nowrap">
                      {a.agreementNumber}
                      {a.title && (
                        <span className="block text-[10px] text-gray-500 font-sans">
                          {a.title}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-200">
                      {a.subInfo?.businessName ||
                        a.subContractor?.displayName ||
                        "—"}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={cls(
                          "px-1.5 py-0.5 text-[9px] font-semibold rounded",
                          a.union === "non-union"
                            ? "bg-gray-700 text-gray-300"
                            : "bg-teal-700 text-white",
                        )}
                      >
                        {a.union === "non-union" ? "NON-UNION" : a.union}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-300 whitespace-nowrap">
                      {a.scheduledDate
                        ? new Date(a.scheduledDate).toLocaleDateString(
                            "en-CA",
                            { timeZone: "UTC" },
                          )
                        : "—"}
                    </td>
                    <td
                      className="px-3 py-2 text-gray-400 max-w-[240px] truncate"
                      title={a.scopeOfWork}
                    >
                      {(a.scopeOfWork || "").replace(/\n/g, " · ")}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={cls(
                          "px-1.5 py-0.5 text-[9px] font-semibold rounded whitespace-nowrap",
                          STATUS_STYLE[a.status],
                        )}
                      >
                        {STATUS_LABEL[a.status]}
                      </span>
                      {a.payrollLocked && a.status !== "paid" && (
                        <span className="block text-[9px] text-purple-300 mt-0.5">
                          In payroll
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-300 font-mono">
                      ${fmt(a.subtotal)}
                    </td>
                    <td
                      className={cls(
                        "px-3 py-2 text-right font-mono font-semibold",
                        a.status === "issued"
                          ? "text-yellow-300"
                          : "text-green-300",
                      )}
                    >
                      ${fmt(a.total)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {a.signedAt ? (
                        <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-green-800/60 text-green-200">
                          🔒 {a.signature?.name ? "E-signed" : "Paper copy"}
                        </span>
                      ) : a.sendInfo?.sentAt ? (
                        <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-blue-800/60 text-blue-200">
                          Sent{" "}
                          {new Date(a.sendInfo.sentAt).toLocaleDateString(
                            "en-CA",
                          )}
                        </span>
                      ) : (
                        <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-gray-700 text-gray-400">
                          Unsigned
                        </span>
                      )}
                      {reversalCount > 0 && (
                        <span
                          className="block text-[9px] text-red-400/80 mt-0.5"
                          title="Signature was previously reversed"
                        >
                          reversed {reversalCount}×
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setModal({ agreement: a });
                        }}
                        className="px-2.5 py-1 rounded text-[11px] font-medium bg-orange-500/20 text-orange-300 hover:bg-orange-500/30 transition-colors"
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <SubAgreementModal
          key={modal.agreement?._id || "new"}
          woId={woId}
          agreement={modal.agreement}
          headers={headers}
          defaultUnion={defaultUnion}
          onClose={() => setModal(null)}
          onChanged={refreshAll}
        />
      )}
    </div>
  );
}
